import {Component} from '@angular/core';
import {BiometryVendor} from '@npck/auth-frontend-api';
import {OauthRedirectErrorCode} from '@npck/errors-api';
import {AnalyticsService} from '@npck/libs/analytics-sdk';
import {isMobile} from '@npck/libs/commons';
import {AuthHolderTokenService} from '../../domain/auth-holder-token.service';
import {AuthHolderService} from '../../domain/auth-holder.service';
import {OauthService} from '../../domain/oauth.service';
import {
    AUTH_VIDEO_IDENTIFICATION_BLOCKED,
    AUTH_VIDEO_IDENTIFICATION_FAILED,
    AUTH_VIDEO_IDENTIFICATION_INIT,
    AUTH_VIDEO_IDENTIFICATION_RETRY,
    AUTH_VIDEO_IDENTIFICATION_STARTED,
    AUTH_VIDEO_IDENTIFICATION_SUCCEED,
} from '../../infra/analytics-events';
import {Error} from '../../infra/app-error-handler.service';
import {QrAppearance} from './liveness-create-qr/liveness-create-qr.component';

@Component({
    templateUrl: 'liveness-device.component.html',
    styleUrls: ['liveness-device.component.scss'],
    standalone: false,
})
export class LivenessDeviceComponent {
    Pages = Pages;
    qrAppearance: QrAppearance;
    isMobile: boolean;
    openPage = Pages.LIVENESS;
    biometryVendor: BiometryVendor = this.authHolderService.biometryVendor;
    retryStatus: LivenessServiceStatus;
    retryIconName: string;
    buttonType: 'primary' | 'secondary';
    private livenessServiceResponse: LivenessServiceResponse;

    constructor(
        private oauthService: OauthService,
        private tokenService: AuthHolderTokenService,
        private authHolderService: AuthHolderService,
        private analyticsService: AnalyticsService,
    ) {
        this.logEvent(AUTH_VIDEO_IDENTIFICATION_INIT);
        this.isMobile = isMobile();
    }

    restartLiveness(): void {
        this.openPage = Pages.LIVENESS;
    }

    showQr(): void {
        this.openPage = Pages.QR;
        this.qrAppearance = QrAppearance.MANUALLY;
    }

    handleLivenessResponse(livenessServiceResponse: LivenessServiceResponse): void {
        this.livenessServiceResponse = livenessServiceResponse;
        this.qrAppearance = null;
        switch (livenessServiceResponse?.status) {
            case LivenessServiceStatus.CLOSED: {
                this.tokenService.handleNextFactor();
                break;
            }
            case LivenessServiceStatus.SUCCEED: {
                this.logEvent(AUTH_VIDEO_IDENTIFICATION_SUCCEED, {
                    reason: livenessServiceResponse.status,
                    detailed_reason: livenessServiceResponse.rootStatus,
                });
                break;
            }
            case LivenessServiceStatus.LIMIT_PER_SESSION_EXCEEDED: {
                this.logEvent(AUTH_VIDEO_IDENTIFICATION_BLOCKED, {
                    reason: livenessServiceResponse.status,
                    detailed_reason: livenessServiceResponse.rootStatus,
                });
                this.oauthService.handleOauthErrorAndRedirect(
                    OauthRedirectErrorCode.liveness_attempts_exceeded,
                    livenessServiceResponse.requestId,
                );
                break;
            }
            case LivenessServiceStatus.CAMERA_FAILED: {
                this.logEvent(AUTH_VIDEO_IDENTIFICATION_FAILED, {
                    reason: livenessServiceResponse.status,
                    detailed_reason: livenessServiceResponse.rootStatus,
                });
                if (!this.authHolderService.authSessionData.showAlternativeLivenessWays && this.isMobile) {
                    this.oauthService.handleOauthError(Error.CAMERA_UNAVAILABLE);
                } else {
                    this.openPage = Pages.QR;
                    this.qrAppearance = QrAppearance.CAMERA_FAILED;
                }
                break;
            }
            case LivenessServiceStatus.FACE_NOT_MATCHED: {
                this.logEvent(AUTH_VIDEO_IDENTIFICATION_FAILED, {
                    reason: livenessServiceResponse.status,
                    detailed_reason: livenessServiceResponse.rootStatus,
                });
                this.oauthService.handleOauthErrorAndRedirect(
                    OauthRedirectErrorCode.gbdfl_face_not_matched,
                );
                break;
            }
            case LivenessServiceStatus.LIVENESS_FAILED: {
                this.logEvent(AUTH_VIDEO_IDENTIFICATION_FAILED, {
                    reason: livenessServiceResponse.status,
                    detailed_reason: livenessServiceResponse.rootStatus,
                });
                this.openPage = Pages.ALTERNATIVE_WAYS_LIVENESS;
                break;
            }
            case LivenessServiceStatus.NETWORK_ERROR: {
                this.logEvent(AUTH_VIDEO_IDENTIFICATION_FAILED, {
                    reason: livenessServiceResponse.status,
                    detailed_reason: livenessServiceResponse.rootStatus,
                });
                this.oauthService.handleOauthErrorAndRedirect(OauthRedirectErrorCode.network_connectivity_error);
                break;
            }
            case LivenessServiceStatus.LIVENESS_ERROR: {
                this.logEvent(AUTH_VIDEO_IDENTIFICATION_FAILED, {
                    reason: livenessServiceResponse.status,
                    detailed_reason: livenessServiceResponse.rootStatus,
                });
                this.oauthService.handleOauthErrorAndRedirect(OauthRedirectErrorCode.liveness_error);
                break;
            }
            case LivenessServiceStatus.NETWORK_PROBLEM: {
                this.openPage = Pages.RETRY;
                this.logEvent(AUTH_VIDEO_IDENTIFICATION_RETRY, {
                    reason: livenessServiceResponse.status,
                    detailed_reason: livenessServiceResponse.rootStatus,
                });
                this.retryStatus = livenessServiceResponse.status;
                this.buttonType = 'secondary';
                this.retryIconName = 'ic_network_warning';
                break;
            }
            case LivenessServiceStatus.LIVENESS_STARTED: {
                this.logEvent(AUTH_VIDEO_IDENTIFICATION_STARTED, {
                    reason: livenessServiceResponse.status,
                    detailed_reason: livenessServiceResponse.rootStatus,
                });
                break;
            }
            case LivenessServiceStatus.CANCEL: {
                this.openPage = Pages.RETRY;
                this.logEvent(AUTH_VIDEO_IDENTIFICATION_FAILED, {
                    reason: livenessServiceResponse.status,
                    detailed_reason: livenessServiceResponse.rootStatus,
                });
                this.buttonType = 'primary';
                this.retryStatus = livenessServiceResponse.status;
                this.retryIconName = 'ic_face_scan';
                return;
            }
            case LivenessServiceStatus.ORIENTATION_ERROR: {
                this.openPage = Pages.RETRY;
                this.logEvent(AUTH_VIDEO_IDENTIFICATION_FAILED, {
                    reason: livenessServiceResponse.status,
                    detailed_reason: livenessServiceResponse.rootStatus,
                });
                this.buttonType = 'secondary';
                this.retryStatus = livenessServiceResponse.status;
                this.retryIconName = 'ic_orientation_error';
                break;
            }
            case LivenessServiceStatus.DEVICE_NOT_SUPPORTED: {
                this.openPage = Pages.RETRY;
                this.logEvent(AUTH_VIDEO_IDENTIFICATION_FAILED, {
                    reason: livenessServiceResponse.status,
                    detailed_reason: livenessServiceResponse.rootStatus,
                });
                this.buttonType = 'secondary';
                this.retryStatus = livenessServiceResponse.status;
                this.retryIconName = 'ic_warning_red';
                break;
            }
        }
    }

    retry(): void {
        if (this.livenessServiceResponse?.status === LivenessServiceStatus.DEVICE_NOT_SUPPORTED) {
            this.openPage = Pages.QR;
            this.qrAppearance = QrAppearance.CAMERA_FAILED;
        } else {
            this.restartLiveness();
        }
    }

    private logEvent(eventName: string, additionalProperties: {[key: string]: string} = {}): void {
        this.analyticsService.capture(eventName, {
            biometry_vendor: this.biometryVendor,
            qr_bool: false,
            ...additionalProperties,
        });
    }
}

export enum Pages {
    ALTERNATIVE_WAYS_LIVENESS = 'ALTERNATIVE_WAYS_LIVENESS',
    QR = 'QR',
    LIVENESS = 'LIVENESS',
    RETRY = 'RETRY'
}

export enum LivenessServiceStatus {
    LIMIT_PER_SESSION_EXCEEDED = 'LIMIT_PER_SESSION_EXCEEDED',
    SUCCEED = 'SUCCEED',
    CLOSED = 'CLOSED',
    CAMERA_FAILED = 'CAMERA_FAILED',
    LIVENESS_FAILED = 'LIVENESS_FAILED',
    FACE_NOT_MATCHED = 'FACE_NOT_MATCHED',
    CANCEL = 'CANCEL',
    LIVENESS_STARTED = 'LIVENESS_STARTED',
    NETWORK_ERROR = 'NETWORK_ERROR',
    NETWORK_PROBLEM = 'NETWORK_PROBLEM',
    LIVENESS_ERROR = 'LIVENESS_ERROR',
    ORIENTATION_ERROR = 'ORIENTATION_ERROR',
    DEVICE_NOT_SUPPORTED = 'DEVICE_NOT_SUPPORTED'
}

export class LivenessServiceResponse {
    status: LivenessServiceStatus;
    requestId?: string;
    rootStatus: string;
}
